import { useLingui } from "@lingui/react/macro";
import type { Bot, BotAbility, BotSettingsDialogSection, UpdateBotInput } from "@rakazo/contracts";
import { BOT_SETTINGS_DIALOG_SECTIONS } from "@rakazo/contracts";
import { BotAvatar, Button, Dialog, DialogClose, DialogContent, DialogTitle } from "@rakazo/ui-web";
import {
  Bell,
  BookUser,
  Brain,
  Cpu,
  Crown,
  KeyRound,
  MessageSquare,
  Sparkles,
  User,
  XIcon,
} from "lucide-react";
import type { ComponentType } from "react";
import { useState } from "react";
import { SETTINGS_CONTENT_WIDTH } from "../../components/SettingsRows";
import { SpaceRpcProvider } from "../../lib/space-rpc";
import { errorText } from "../../lib/user-error";
import {
  AbilitiesSection,
  AddressBookSection,
  ConversationSection,
  MemorySection,
  ModelSection,
  NotificationsSection,
  ProfileSection,
  SuperBotSection,
  VaultSection,
  withSettingDefaults,
} from "./bot-settings-sections";

export type BotSettingsActions = {
  /** Saves only the fields given; the parent swaps in the bot the server returns. */
  save: (patch: Omit<UpdateBotInput, "botId">) => Promise<void>;
  setAbility: (ability: BotAbility, enabled: boolean) => Promise<void>;
  setComputer: (mode: Bot["computerMode"]) => Promise<void>;
  onExport: () => Promise<void>;
  onClear: () => void;
  onOpenConnections?: () => void;
};

/** The owner's models, notifications and vault; a Space admin does not change them. */
const OWNER_ONLY_SECTIONS: ReadonlySet<BotSettingsDialogSection> = new Set([
  "model",
  "notifications",
  "vault",
]);

const ICONS: Record<BotSettingsDialogSection, ComponentType<{ className?: string }>> = {
  profile: User,
  model: Cpu,
  abilities: Sparkles,
  superBot: Crown,
  notifications: Bell,
  memory: Brain,
  vault: KeyRound,
  addressBook: BookUser,
  conversation: MessageSquare,
};

/** One bot's settings, a section at a time. Every control saves as soon as it changes. */
export function BotSettingsDialog({
  bot,
  initialSection = "profile",
  currentSuperBot,
  actions,
  onClose,
}: {
  bot: Bot;
  initialSection?: BotSettingsDialogSection;
  /** The name of the Space's other bot that is the Super Nexa Mate now, if any. */
  currentSuperBot?: string;
  actions: BotSettingsActions;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const [section, setSection] = useState<BotSettingsDialogSection>(initialSection);
  const [error, setError] = useState<string | null>(null);
  const labels: Record<BotSettingsDialogSection, string> = {
    profile: t`Profile`,
    model: t`Model`,
    abilities: t`Abilities`,
    superBot: t`Super Nexa Mate`,
    notifications: t`Notifications and voice`,
    memory: t`Memory and skills`,
    vault: t`Vault`,
    addressBook: t`Address book`,
    conversation: t`Conversation`,
  };
  // A failed save shows once, under the section it came from, and clears on the next change.
  const guarded =
    <A extends unknown[]>(run: (...args: A) => Promise<void>) =>
    async (...args: A) => {
      setError(null);
      try {
        await run(...args);
      } catch (err) {
        setError(errorText(err, t`Could not save`));
        throw err;
      }
    };
  const sectionActions: BotSettingsActions = {
    ...actions,
    save: guarded(actions.save),
    setAbility: guarded(actions.setAbility),
    setComputer: guarded(actions.setComputer),
    onExport: guarded(actions.onExport),
  };
  const props = { bot: withSettingDefaults(bot), actions: sectionActions };
  // A Space admin on another member's shared bot sees its job, not the owner's own settings.
  const sections =
    bot.access === "manage"
      ? BOT_SETTINGS_DIALOG_SECTIONS.filter((id) => !OWNER_ONLY_SECTIONS.has(id))
      : BOT_SETTINGS_DIALOG_SECTIONS;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        data-testid="bot-settings"
        data-settings-section={section}
        showCloseButton={false}
        className="flex h-[min(720px,calc(100%-2rem))] w-[min(980px,calc(100%-2rem))] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-[980px]"
      >
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <nav
            aria-label={t`Nexa Mate settings`}
            className="flex shrink-0 flex-row gap-1 overflow-x-auto border-b border-border px-3 py-3 md:w-[220px] md:flex-col md:overflow-y-auto md:border-e md:border-b-0 md:py-4"
          >
            <div className="hidden items-center gap-2.5 px-2.5 pb-3 md:flex">
              <BotAvatar color={bot.color} identity={bot.id} avatarSet={bot.avatarSet} size={28} />
              <span className="min-w-0 truncate text-sm font-medium">{bot.name}</span>
            </div>
            {sections.map((id) => {
              const Icon = ICONS[id];
              const active = id === section;
              return (
                <button
                  key={id}
                  type="button"
                  data-testid={`bot-settings-nav-${id}`}
                  aria-current={active ? "page" : undefined}
                  onClick={() => {
                    setError(null);
                    setSection(id);
                  }}
                  className={`flex shrink-0 items-center gap-2.5 rounded-lg px-2.5 py-2 text-start text-[13.5px] transition-colors ${
                    active
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                >
                  <Icon className="size-4 shrink-0" />
                  <span className="whitespace-nowrap">{labels[id]}</span>
                </button>
              );
            })}
          </nav>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="flex shrink-0 items-center justify-between gap-4 border-b border-border px-6 py-5 sm:px-8">
              <DialogTitle className="text-heading2 font-medium">{labels[section]}</DialogTitle>
              <DialogClose aria-label={t`Close`} render={<Button variant="ghost" size="icon-sm" />}>
                <XIcon />
              </DialogClose>
            </div>
            <SpaceRpcProvider spaceId={bot.spaceId}>
              <div className="rk-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-6 sm:px-8">
                <div className={`${SETTINGS_CONTENT_WIDTH} space-y-6`}>
                  {section === "profile" ? <ProfileSection key={bot.id} {...props} /> : null}
                  {section === "model" ? <ModelSection key={bot.id} {...props} /> : null}
                  {section === "abilities" ? <AbilitiesSection key={bot.id} {...props} /> : null}
                  {section === "superBot" ? (
                    <SuperBotSection {...props} currentSuperBot={currentSuperBot} />
                  ) : null}
                  {section === "notifications" ? <NotificationsSection {...props} /> : null}
                  {section === "memory" ? <MemorySection {...props} /> : null}
                  {section === "vault" ? <VaultSection /> : null}
                  {section === "addressBook" ? <AddressBookSection botId={bot.id} /> : null}
                  {section === "conversation" ? <ConversationSection {...props} /> : null}
                  {error ? (
                    <p role="alert" className="text-[13px] text-destructive">
                      {error}
                    </p>
                  ) : null}
                </div>
              </div>
            </SpaceRpcProvider>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
